import { useState, useRef, useEffect } from "react";
import { X, Pencil, Trash2, Check, Loader2, CreditCard, Barcode as BarcodeIcon, AlertTriangle, Info, Lock, Plus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import JsBarcode from "jsbarcode";
import { CARD_PALETTE, getGroupTypeConfig } from "../utils/formatters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRIMARY = "#2D5BFF";
const PRIMARY_DARK = "#1B3DBF";

const getPalette = (card) => {
    let idx = Number.isInteger(card?.colour_index) ? card.colour_index : 0;
    idx = ((idx % 8) + 8) % 8;
    return CARD_PALETTE[idx];
};

const buildGradient = (palette) =>
    `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)`;

const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
};

// ── Known UK loyalty card brand colours ─────────────────────────────────────
export const BRAND_COLOURS = {
    "tesco":       { from: "#00539F", to: "#003F7A" },
    "clubcard":    { from: "#00539F", to: "#003F7A" },
    "nectar":      { from: "#6B2B8A", to: "#4A1A63" },
    "sainsbury":   { from: "#F06C00", to: "#C45200" },
    "boots":       { from: "#0082C8", to: "#005A8E" },
    "advantage":   { from: "#0082C8", to: "#005A8E" },
    "morrisons":   { from: "#1E7A45", to: "#125230" },
    "more":        { from: "#1E7A45", to: "#125230" },
    "b&q":         { from: "#F36D21", to: "#C45218" },
    "asda":        { from: "#78BE20", to: "#4E8A00" },
    "starbucks":   { from: "#00704A", to: "#004E35" },
    "costa":       { from: "#6C1F45", to: "#4B1530" },
    "greggs":      { from: "#005BAA", to: "#003D7A" },
    "waitrose":    { from: "#71A860", to: "#4E7F43" },
    "m&s":         { from: "#2F2F2F", to: "#111111" },
    "marks":       { from: "#2F2F2F", to: "#111111" },
    "superdrug":   { from: "#E8005E", to: "#A8003F" },
    "pret":        { from: "#862633", to: "#5C1922" },
    "nandos":      { from: "#C8102E", to: "#8C0B1F" },
    "bp":          { from: "#007A33", to: "#005224" },
    "shell":       { from: "#E0001A", to: "#B00014" },
    "ikea":        { from: "#0058A3", to: "#003D73" },
    "waterstones": { from: "#2C5F3F", to: "#1A3D28" },
    "holland":     { from: "#6CB33F", to: "#4A8028" },
};

export const getBrandGradient = (cardName) => {
    if (!cardName) return null;
    const lower = cardName.toLowerCase();
    for (const [key, colours] of Object.entries(BRAND_COLOURS)) {
        if (lower.includes(key)) return colours;
    }
    return null;
};

const BarcodeDisplay = ({ value }) => {
    const svgRef = useRef(null);

    useEffect(() => {
        if (!svgRef.current || !value) return;
        try {
            JsBarcode(svgRef.current, value, {
                format: "CODE128",
                width: 1.8,
                height: 90,
                displayValue: false,
                margin: 10,
                lineColor: "#000000",
                background: "#FFFFFF",
            });
        } catch {
            // value not valid for detected format
        }
    }, [value]);

    if (!value) return null;

    return (
        <div style={{ backgroundColor: "#FFFFFF", borderRadius: 10, padding: "12px 16px" }}>
            <p style={{ color: "#94A3B8", fontSize: 10, fontWeight: 800, letterSpacing: 1.6, fontFamily: "DM Sans, sans-serif", textAlign: "center", marginBottom: 10 }}>
                SHOW AT CHECKOUT
            </p>
            <svg ref={svgRef} style={{ width: "100%", display: "block" }} />
            <p style={{ color: "#64748B", fontSize: 12, fontFamily: "DM Sans, sans-serif", textAlign: "center", marginTop: 8, letterSpacing: 2 }}>
                {value.replace(/(.{4})/g, "$1 ").trim()}
            </p>
        </div>
    );
};

export default function CardDetailSheet({
    card,
    isOpen,
    onClose,
    onDeleted,
    onUpdated,
    isOwner = true,
    sharedByName = null,
    sharingInfo = [],
}) {
    const overlayRef = useRef(null);

    // Edit state
    const [editing, setEditing] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editName, setEditName] = useState("");
    const [editBarcode, setEditBarcode] = useState("");
    const [editNotes, setEditNotes] = useState("");
    const [errors, setErrors] = useState({});

    // Group sharing state (owner only)
    const [cardShares, setCardShares] = useState([]);
    const [sharingLoading, setSharingLoading] = useState(false);
    const [showGroupPicker, setShowGroupPicker] = useState(false);
    const [allGroups, setAllGroups] = useState([]);
    const [removingShareId, setRemovingShareId] = useState(null);
    const [sharingGroupId, setSharingGroupId] = useState(null);

    // Reset on open/card change
    useEffect(() => {
        if (!isOpen || !card) return;
        setEditing(false);
        setDeleting(false);
        setConfirmDelete(false);
        setEditName(card.card_name || "");
        setEditBarcode(card.barcode_number || "");
        setEditNotes(card.notes || "");
        setErrors({});
        setShowGroupPicker(false);
        document.body.style.overflow = "hidden";
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "unset";
            document.removeEventListener("keydown", onKey);
        };
    }, [isOpen, card, onClose]);

    // Fetch card's sharing info (owner only)
    useEffect(() => {
        if (!isOpen || !card || !isOwner) { setCardShares([]); return; }
        const fetchSharing = async () => {
            setSharingLoading(true);
            try {
                const res = await axios.get(`${API}/my-shared-items?item_id=${card.id}`, { withCredentials: true });
                setCardShares((res.data.shared_items || []).filter(s => s.item_type === "card"));
            } catch { /* silent */ }
            finally { setSharingLoading(false); }
        };
        fetchSharing();
    }, [isOpen, card, isOwner]);

    // Fetch all groups for picker (owner only)
    useEffect(() => {
        if (!isOpen || !card || !isOwner) { setAllGroups([]); return; }
        const fetchGroups = async () => {
            try {
                const res = await axios.get(`${API}/groups`, { withCredentials: true });
                setAllGroups(res.data.groups || []);
            } catch { /* silent */ }
        };
        fetchGroups();
    }, [isOpen, card, isOwner]);

    if (!isOpen || !card) return null;

    const palette = getPalette(card);
    const brandGrad = getBrandGradient(card.card_name);
    const gradient = brandGrad
        ? `linear-gradient(135deg, ${brandGrad.from} 0%, ${brandGrad.to} 100%)`
        : buildGradient(palette);
    const initials = getInitials(card.card_name);

    // Groups available in picker: loyalty_cards enabled + not already shared
    const sharedGroupIds = new Set(cardShares.map(s => s.group_id));
    const pickerGroups = allGroups.filter(g =>
        g.sharing?.loyalty_cards === true && !sharedGroupIds.has(g.id)
    );

    // Group chips to display (owner: from cardShares; non-owner: from sharingInfo prop)
    const groupChips = isOwner ? cardShares : sharingInfo;

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axios.delete(`${API}/cards/${card.id}`, { withCredentials: true });
            toast.success("Card deleted");
            onDeleted?.(card.id);
            onClose();
        } catch (err) {
            console.error("Delete card error:", err);
            toast.error("Couldn't delete card");
        } finally {
            setDeleting(false);
        }
    };

    const handleSave = async () => {
        const errs = {};
        if (!editName.trim()) errs.card_name = "Enter card name";
        if (!editBarcode.trim()) errs.barcode_number = "Enter card number";
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setSaving(true);
        try {
            const res = await axios.put(
                `${API}/cards/${card.id}`,
                { card_name: editName.trim(), barcode_number: editBarcode.trim(), notes: editNotes.trim() || null },
                { withCredentials: true }
            );
            if (res.data.success) {
                toast.success("Card updated");
                onUpdated?.(res.data.card);
                onClose();
            }
        } catch (err) {
            console.error("Update card error:", err);
            toast.error("Couldn't update card");
        } finally {
            setSaving(false);
        }
    };

    const handleShare = async (groupId) => {
        setSharingGroupId(groupId);
        try {
            await axios.post(
                `${API}/groups/${groupId}/share`,
                { item_id: card.id, item_type: "card" },
                { withCredentials: true }
            );
            // Refresh sharing info
            const res = await axios.get(`${API}/my-shared-items?item_id=${card.id}`, { withCredentials: true });
            setCardShares((res.data.shared_items || []).filter(s => s.item_type === "card"));
            setShowGroupPicker(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't share card");
        } finally {
            setSharingGroupId(null);
        }
    };

    const handleUnshare = async (shareId, groupId) => {
        setRemovingShareId(shareId);
        try {
            await axios.delete(`${API}/groups/${groupId}/share/${shareId}`, { withCredentials: true });
            setCardShares(prev => prev.filter(s => s.share_id !== shareId));
        } catch {
            toast.error("Couldn't unshare card");
        } finally {
            setRemovingShareId(null);
        }
    };

    return (
        <div
            ref={overlayRef}
            onClick={(e) => e.target === overlayRef.current && onClose()}
            className="fixed inset-0 z-50 flex items-end justify-center animate-fadeIn"
            style={{ backgroundColor: "rgba(15,23,42,0.55)" }}
            data-testid="card-detail-overlay"
        >
            <div
                className="w-full bg-white flex flex-col animate-slideUp"
                style={{
                    borderTopLeftRadius: 22,
                    borderTopRightRadius: 22,
                    maxHeight: "92vh",
                    overflow: "hidden",
                    maxWidth: 480,
                }}
                data-testid="card-detail-sheet"
            >
                {/* Drag handle */}
                <div className="flex justify-center pt-2.5 pb-2 flex-shrink-0">
                    <div style={{ width: 40, height: 4, backgroundColor: "#D1D5DB", borderRadius: 999 }} />
                </div>

                {/* Title + close */}
                <div className="flex items-center justify-between px-5 pb-1 flex-shrink-0">
                    <p style={{ color: "#0F172A", fontSize: 17, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                        {editing ? "Edit card" : "Card details"}
                    </p>
                    <button
                        onClick={onClose}
                        className="flex items-center justify-center"
                        style={{ width: 34, height: 34, borderRadius: 999, backgroundColor: "#F1F5F9", color: "#64748B" }}
                        data-testid="card-detail-close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-4" style={{ paddingTop: 8 }}>
                    {/* Card visual */}
                    <div
                        className="relative w-full overflow-hidden"
                        style={{ height: 160, borderRadius: 20, background: gradient, boxShadow: "0 10px 28px rgba(15,23,42,0.2)" }}
                    >
                        <div className="absolute" style={{ right: -50, bottom: -50, width: 220, height: 220, borderRadius: 999, background: "radial-gradient(circle, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 70%)" }} />
                        <div className="relative h-full flex flex-col justify-between" style={{ padding: 18 }}>
                            <div className="flex items-start justify-between">
                                <div
                                    className="flex items-center justify-center"
                                    style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: "rgba(255,255,255,0.22)", color: "#FFFFFF", fontSize: 14, fontWeight: 800, fontFamily: "Manrope, sans-serif" }}
                                >
                                    {initials}
                                </div>
                                <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 11, fontWeight: 700, letterSpacing: 1.4, fontFamily: "DM Sans, sans-serif" }}>
                                    LOYALTY
                                </span>
                            </div>
                            <div>
                                <p style={{ color: "#FFFFFF", fontSize: 24, fontWeight: 800, fontFamily: "Manrope, sans-serif", letterSpacing: -0.3 }}>
                                    {card.card_name}
                                </p>
                                {card.barcode_number && (
                                    <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif", marginTop: 3, letterSpacing: 1 }}>
                                        {card.barcode_number}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Group chips */}
                    {groupChips.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {groupChips.map(g => {
                                const cfg = getGroupTypeConfig(g.group_type);
                                const Icon = cfg.icon;
                                return (
                                    <div key={g.group_id || g.share_id} className="flex items-center gap-1.5" style={{ backgroundColor: cfg.bg, borderRadius: 999, padding: "5px 12px 5px 6px" }}>
                                        <div className="flex items-center justify-center" style={{ width: 20, height: 20, borderRadius: 999, backgroundColor: "rgba(255,255,255,0.2)", fontSize: 12 }}>
                                            {g.emoji
                                                ? <span style={{ lineHeight: 1 }}>{g.emoji}</span>
                                                : <Icon className="w-3 h-3" style={{ color: "#FFFFFF" }} />}
                                        </div>
                                        <span style={{ color: "#FFFFFF", fontSize: 12, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                            {g.group_name}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {!editing ? (
                        <>
                            {/* Barcode */}
                            <BarcodeDisplay value={card.barcode_number} />

                            {/* Card info rows */}
                            <div style={{ backgroundColor: "#F8FAFC", borderRadius: 14, overflow: "hidden" }}>
                                <div className="flex items-center justify-between" style={{ padding: "14px 16px", borderBottom: "1px solid #E2E8F0" }}>
                                    <span style={{ color: "#64748B", fontSize: 14, fontFamily: "DM Sans, sans-serif" }}>Card number</span>
                                    <span style={{ color: "#0F172A", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>
                                        {card.barcode_number || "—"}
                                    </span>
                                </div>
                                {card.notes && (
                                    <div className="flex items-center justify-between" style={{ padding: "14px 16px" }}>
                                        <span style={{ color: "#64748B", fontSize: 14, fontFamily: "DM Sans, sans-serif" }}>Notes</span>
                                        <span style={{ color: "#0F172A", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif", maxWidth: "65%", textAlign: "right" }}>
                                            {card.notes}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Shared-by banner — non-owner only */}
                            {!isOwner && (
                                <div className="flex items-start gap-2.5" style={{ padding: "12px 16px", borderRadius: 14, backgroundColor: "#EEF2FF", border: "1px solid #C7D2FE" }}>
                                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#4338CA" }} />
                                    <p style={{ color: "#3730A3", fontSize: 13, fontFamily: "DM Sans, sans-serif", lineHeight: 1.5 }}>
                                        Shared by <strong>{sharedByName || "a group member"}</strong> · You can view but not edit
                                    </p>
                                </div>
                            )}

                            {/* Group sharing section — owner only */}
                            {isOwner && (
                                <div style={{ backgroundColor: "#F8FAFC", borderRadius: 16, overflow: "hidden" }}>
                                    {/* Header */}
                                    <div style={{ padding: "14px 16px", borderBottom: "1px solid #E2E8F0" }}>
                                        <p style={{ color: "#0F172A", fontSize: 15, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>Circle sharing</p>
                                        <p style={{ color: "#94A3B8", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 2 }}>Stays in Personal · shared as a link</p>
                                    </div>

                                    {/* Household-only note */}
                                    <div className="flex items-center gap-2" style={{ padding: "10px 16px", backgroundColor: "#EEF2FF", borderBottom: "1px solid #E2E8F0" }}>
                                        <Info className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#4338CA" }} />
                                        <p style={{ color: "#4338CA", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>Cards can only be shared with Household circles</p>
                                    </div>

                                    {/* Personal row */}
                                    <div className="flex items-center justify-between" style={{ padding: "12px 16px", borderBottom: "1px solid #E2E8F0" }}>
                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 999, backgroundColor: "#EEF2FF" }}>
                                                <Lock className="w-4 h-4" style={{ color: PRIMARY }} />
                                            </div>
                                            <div>
                                                <p style={{ color: "#0F172A", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>Personal</p>
                                                <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>Only you can see this</p>
                                            </div>
                                        </div>
                                        <span style={{ color: "#94A3B8", fontSize: 12, fontWeight: 600, fontFamily: "DM Sans, sans-serif", backgroundColor: "#F1F5F9", padding: "4px 10px", borderRadius: 999 }}>
                                            Private
                                        </span>
                                    </div>

                                    {/* Shared groups */}
                                    {sharingLoading ? (
                                        <div className="flex items-center justify-center" style={{ padding: 20 }}>
                                            <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#94A3B8" }} />
                                        </div>
                                    ) : (
                                        <>
                                            {cardShares.map(s => {
                                                const cfg = getGroupTypeConfig(s.group_type);
                                                const Icon = cfg.icon;
                                                return (
                                                    <div key={s.share_id} className="flex items-center justify-between" style={{ padding: "12px 16px", borderBottom: "1px solid #E2E8F0" }}>
                                                        <div className="flex items-center gap-3">
                                                            <div className="flex items-center justify-center flex-shrink-0" style={{ width: 36, height: 36, borderRadius: 999, backgroundColor: cfg.bg, fontSize: 18 }}>
                                                                {s.emoji
                                                                    ? <span style={{ lineHeight: 1 }}>{s.emoji}</span>
                                                                    : <Icon className="w-4 h-4" style={{ color: "#FFFFFF" }} />}
                                                            </div>
                                                            <div>
                                                                <p style={{ color: "#0F172A", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>{s.group_name}</p>
                                                                <p style={{ color: "#16A34A", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>
                                                                    Shared · {s.member_count || 0} members can see this
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => handleUnshare(s.share_id, s.group_id)}
                                                            disabled={removingShareId === s.share_id}
                                                            className="flex items-center justify-center"
                                                            style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: "#FEE2E2" }}
                                                        >
                                                            {removingShareId === s.share_id
                                                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: "#DC2626" }} />
                                                                : <X className="w-3.5 h-3.5" style={{ color: "#DC2626" }} />}
                                                        </button>
                                                    </div>
                                                );
                                            })}

                                            {/* Picker trigger / picker list */}
                                            {!showGroupPicker ? (
                                                pickerGroups.length > 0 && (
                                                    <button
                                                        onClick={() => setShowGroupPicker(true)}
                                                        className="w-full flex items-center gap-3"
                                                        style={{ padding: "12px 16px" }}
                                                    >
                                                        <div className="flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 999, backgroundColor: "#EEF2FF" }}>
                                                            <Plus className="w-4 h-4" style={{ color: PRIMARY }} />
                                                        </div>
                                                        <p style={{ color: PRIMARY, fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                                            Share with another group
                                                        </p>
                                                    </button>
                                                )
                                            ) : (
                                                <div>
                                                    {pickerGroups.map(g => {
                                                        const cfg = getGroupTypeConfig(g.type);
                                                        const Icon = cfg.icon;
                                                        return (
                                                            <button
                                                                key={g.id}
                                                                onClick={() => handleShare(g.id)}
                                                                disabled={!!sharingGroupId}
                                                                className="w-full flex items-center gap-3"
                                                                style={{ padding: "12px 16px", borderTop: "1px solid #E2E8F0", opacity: sharingGroupId && sharingGroupId !== g.id ? 0.5 : 1 }}
                                                            >
                                                                <div className="flex items-center justify-center flex-shrink-0" style={{ width: 36, height: 36, borderRadius: 999, backgroundColor: cfg.bg, fontSize: 18 }}>
                                                                    {g.emoji
                                                                        ? <span style={{ lineHeight: 1 }}>{g.emoji}</span>
                                                                        : <Icon className="w-4 h-4" style={{ color: "#FFFFFF" }} />}
                                                                </div>
                                                                <div className="flex-1 text-left">
                                                                    <p style={{ color: "#0F172A", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>{g.name}</p>
                                                                    <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>{cfg.label} · {g.member_count} members</p>
                                                                </div>
                                                                {sharingGroupId === g.id && <Loader2 className="w-4 h-4 animate-spin" style={{ color: PRIMARY }} />}
                                                            </button>
                                                        );
                                                    })}
                                                    <button
                                                        onClick={() => setShowGroupPicker(false)}
                                                        className="w-full flex items-center justify-center"
                                                        style={{ padding: "12px 16px", color: "#64748B", fontSize: 13, fontFamily: "DM Sans, sans-serif" }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Action buttons — owner only */}
                            {isOwner && !confirmDelete && (
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setEditing(true)}
                                        className="flex-1 flex items-center justify-center gap-2 rounded-2xl"
                                        style={{ height: 48, backgroundColor: "#EEF2FF", color: PRIMARY, fontSize: 14, fontWeight: 700, fontFamily: "DM Sans, sans-serif" }}
                                        data-testid="edit-card-btn"
                                    >
                                        <Pencil className="w-4 h-4" />
                                        Edit card
                                    </button>
                                    <button
                                        onClick={() => setConfirmDelete(true)}
                                        className="flex-1 flex items-center justify-center gap-2 rounded-2xl"
                                        style={{ height: 48, backgroundColor: "#FEF2F2", color: "#DC2626", fontSize: 14, fontWeight: 700, fontFamily: "DM Sans, sans-serif" }}
                                        data-testid="delete-card-btn"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Delete
                                    </button>
                                </div>
                            )}

                            {isOwner && confirmDelete && (
                                <div style={{ backgroundColor: "#FEF2F2", borderRadius: 16, border: "1px solid #FECACA", padding: 16 }}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: "#DC2626" }} />
                                        <p style={{ color: "#DC2626", fontSize: 14, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                                            Delete {card.card_name}?
                                        </p>
                                    </div>
                                    <p style={{ color: "#9F1239", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginBottom: 14, lineHeight: 1.5 }}>
                                        This will permanently remove the card. You can always re-add it.
                                    </p>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setConfirmDelete(false)}
                                            className="flex-1 rounded-xl"
                                            style={{ height: 42, backgroundColor: "#FFFFFF", color: "#374151", fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif", border: "1px solid #E5E7EB" }}
                                            data-testid="cancel-delete-btn"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleDelete}
                                            disabled={deleting}
                                            className="flex-1 flex items-center justify-center gap-1.5 rounded-xl"
                                            style={{ height: 42, backgroundColor: "#DC2626", color: "#FFFFFF", fontSize: 13, fontWeight: 700, fontFamily: "DM Sans, sans-serif", opacity: deleting ? 0.7 : 1 }}
                                            data-testid="confirm-delete-card-btn"
                                        >
                                            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                            {deleting ? "Deleting…" : "Yes, delete"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        /* Edit form — only reachable by owner */
                        <div className="space-y-4">
                            <div>
                                <label style={{ color: "#0F172A", fontSize: 13, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>Card name</label>
                                <div className="relative mt-2">
                                    <CreditCard className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#94A3B8" }} />
                                    <input
                                        type="text"
                                        value={editName}
                                        onChange={(e) => { setEditName(e.target.value); setErrors((p) => ({ ...p, card_name: null })); }}
                                        className="w-full rounded-xl"
                                        style={{ height: 48, paddingLeft: 38, paddingRight: 12, border: `1px solid ${errors.card_name ? "#DC2626" : "#E2E8F0"}`, fontSize: 15, fontFamily: "DM Sans, sans-serif", outline: "none" }}
                                        data-testid="edit-card-name-input"
                                    />
                                </div>
                                {errors.card_name && <p style={{ color: "#DC2626", fontSize: 12, marginTop: 4 }}>{errors.card_name}</p>}
                            </div>

                            <div>
                                <label style={{ color: "#0F172A", fontSize: 13, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>Card number</label>
                                <div className="relative mt-2">
                                    <BarcodeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#94A3B8" }} />
                                    <input
                                        type="text"
                                        value={editBarcode}
                                        onChange={(e) => { setEditBarcode(e.target.value); setErrors((p) => ({ ...p, barcode_number: null })); }}
                                        className="w-full rounded-xl"
                                        style={{ height: 48, paddingLeft: 38, paddingRight: 12, border: `1px solid ${errors.barcode_number ? "#DC2626" : "#E2E8F0"}`, fontSize: 15, fontFamily: "DM Sans, sans-serif", outline: "none" }}
                                        data-testid="edit-card-number-input"
                                    />
                                </div>
                                {errors.barcode_number && <p style={{ color: "#DC2626", fontSize: 12, marginTop: 4 }}>{errors.barcode_number}</p>}
                            </div>

                            <div>
                                <label style={{ color: "#0F172A", fontSize: 13, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                                    Notes <span style={{ color: "#94A3B8", fontWeight: 500 }}>(optional)</span>
                                </label>
                                <textarea
                                    value={editNotes}
                                    onChange={(e) => setEditNotes(e.target.value)}
                                    rows={3}
                                    className="w-full rounded-xl mt-2"
                                    style={{ padding: 12, border: "1px solid #E2E8F0", fontSize: 15, fontFamily: "DM Sans, sans-serif", outline: "none", resize: "none" }}
                                    data-testid="edit-card-notes-input"
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setEditing(false)}
                                    className="flex-shrink-0 flex items-center justify-center rounded-2xl"
                                    style={{ height: 48, paddingLeft: 20, paddingRight: 20, border: "1px solid #E2E8F0", color: "#64748B", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}
                                    data-testid="cancel-edit-btn"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="flex-1 flex items-center justify-center gap-2 rounded-2xl"
                                    style={{ height: 48, background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`, color: "#FFFFFF", fontSize: 14, fontWeight: 700, fontFamily: "DM Sans, sans-serif", opacity: saving ? 0.6 : 1 }}
                                    data-testid="save-edit-btn"
                                >
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                    {saving ? "Saving..." : "Save changes"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
      `}</style>
        </div>
    );
}